import { Alert, CircularProgress, Stack, Typography } from "@mui/material";
import { useMemo } from "react";
import { useTenant } from "hooks/settings";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { useGetDatasetSizeQuery } from "src/store/api/generatedApi";
import ScrapTrackingViewportPlot from "components/scrapTracking/ScrapTrackingViewportPlot";
import type { Layout } from "plotly.js";
import { SummaryChip } from "./SummaryChip";
import { buildCumulativeTraces } from "./datasetSizeTraces";

type DatasetSizeSectionProps = Readonly<{
  selectedLocation: string;
  enabledAngleNames: string[];
}>;

const BYTES_PER_GIB = 1024 ** 3;
const RAM_THRESHOLD_GIB_FALLBACK = 512;

export const DatasetSizeSection = ({
  selectedLocation,
  enabledAngleNames,
}: DatasetSizeSectionProps) => {
  const tenant = useTenant();
  const { t } = useTenantTranslation();
  const formatNumber = useFormatNumber();

  const anglesJson =
    enabledAngleNames.length > 0
      ? JSON.stringify(enabledAngleNames)
      : undefined;

  const {
    data: datasetSizeResponse,
    isLoading,
    isError,
  } = useGetDatasetSizeQuery(
    {
      tenantName: tenant,
      targetLocation: selectedLocation,
      anglesJson,
    },
    { skip: !selectedLocation }
  );

  const { traces, totalGib, ramThresholdGib, layout } = useMemo(() => {
    const windows = datasetSizeResponse?.windows ?? [];
    const traces = buildCumulativeTraces(windows);
    const totalGib =
      (datasetSizeResponse?.cumulative_size_bytes ?? 0) / BYTES_PER_GIB;
    const ramThresholdGib =
      datasetSizeResponse?.ram_threshold_bytes != null
        ? datasetSizeResponse.ram_threshold_bytes / BYTES_PER_GIB
        : RAM_THRESHOLD_GIB_FALLBACK;

    const layout: Partial<Layout> = {
      yaxis: {
        title: { text: t("datasetSizeCumulativeAxis") },
        rangemode: "tozero",
      },
      xaxis: { title: { text: t("datasetSizeDateAxis") } },
      shapes: [
        {
          type: "line",
          x0: 0,
          x1: 1,
          xref: "paper",
          y0: ramThresholdGib,
          y1: ramThresholdGib,
          line: { color: "red", width: 2, dash: "dash" },
        },
      ],
      annotations: [
        {
          x: 1,
          xref: "paper",
          y: ramThresholdGib,
          text: t("datasetSizeRamLimit", { threshold: ramThresholdGib }),
          showarrow: false,
          yshift: 12,
          font: { color: "red", size: 11 },
        },
      ],
      showlegend: true,
      legend: { orientation: "h", y: -0.2 },
    };

    return { traces, totalGib, ramThresholdGib, layout };
  }, [datasetSizeResponse, t]);

  const totalSegments = useMemo(
    () =>
      (datasetSizeResponse?.windows ?? []).reduce(
        (sum, w) => sum + w.segment_count,
        0
      ),
    [datasetSizeResponse]
  );

  if (!selectedLocation) return null;

  return (
    <Stack spacing={2}>
      <Stack>
        <Typography variant="h5" fontWeight={600} color="text.primary">
          {t("datasetSizeTitle")}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t("datasetSizeEmbeddingsOnly")}
        </Typography>
      </Stack>

      <Stack direction="row" spacing={4}>
        <SummaryChip
          label={t("datasetSizeTotalSegments")}
          value={totalSegments}
        />
        <SummaryChip
          label={t("datasetSizeTotalGib")}
          value={formatNumber(1)(totalGib)}
        />
        <SummaryChip
          label={t("datasetSizeRamUsage")}
          value={`${formatNumber(1)((totalGib / ramThresholdGib) * 100)}%`}
          color={
            totalGib / ramThresholdGib > 0.85
              ? "error.main"
              : totalGib / ramThresholdGib > 0.7
                ? "warning.main"
                : "success.main"
          }
        />
      </Stack>

      {isLoading ? (
        <Stack alignItems="center" py={4}>
          <CircularProgress size={24} />
        </Stack>
      ) : isError ? (
        <Alert severity="error">{t("datasetSizeError")}</Alert>
      ) : traces.length === 0 ? (
        <Alert severity="info">{t("datasetSizeNoData")}</Alert>
      ) : (
        <ScrapTrackingViewportPlot
          data={traces}
          layout={layout}
          style={{ width: "100%", height: 400 }}
        />
      )}
    </Stack>
  );
};
